/**
 * @module cercaliagl/Label
 */
import maplibregl from 'maplibre-gl';
import { createUid } from './util';
/**
 * @typedef {Object} LabelOptions
 * @property {string} [className] Class name for CSS styling. Default empty.
 * @property {string} [color] Label color. Default value '#000000'
 * @property {string} [background] Label backgroud. The string format is the same than css tag. Default value 'none repeat scroll 0% 0% #ffffff'
 * @property {boolean} [fontWeight] Bold text. Default: false
 * @property {Array.<number>} [offset] Label offset, in pixels, from the bottom position of icon. Default value for default label is `[-3, -18]`.
 * @property {boolean} [show] Show text. Default value `1`.
 * @property {string} text Simple Label text (with no background).
 * @property {string|undefined} [anchor] Anchor `'center' ,  'top' ,  'bottom' , 'left' ,  'right' ,  'top-left' ,  'top-right'`.
 */
/**
 * @classdesc
  * @classdesc
 * <b>Javascript ES5</b>
 * ```js
 * const label = new cercaliagl.Label({...});
 * ```
 *
 * This class is used to provide a floating overlay `div` HTML on {@link module:cercaliagl/Marker~Marker Marker}. This element is and DOM element, it is different
 * than {@link module:cercaliagl/SimpleLabel~SimpleLabel SimpleLabel} which is rendered in canvas via `webgl`.
 *
 * Example usage:
 *
 * ```js
 * const label = new cercaliagl.Label({
 *   text: 'This is a label',
 *   className: 'cercalia-label-custom-class',
 * });
 * const marker = new cercaliagl.Marker({
 *   position: new cercaliagl.LonLat(1.412, 41.1),
 *   label: label,
 * });
 * map.addMarker(marker);
 * ```
 *
 * @api
 */
var Label = /** @class */ (function () {
    /**
     * @constructor
     * @param {LabelOptions} options Options
     */
    function Label(options) {
        /**
         * @private
         * @type {string}
         */
        this.id_ = "Label".concat(createUid());
        /**
         * @private
         * @type {string}
         */
        this.text_ = options.text ? options.text : '';
        /**
         * @private
         * @type {string}
         */
        this.className_ = options.className ? options.className.trimStart() : '';
        /**
         * @private
         * @type {boolean}
         */
        this.show_ = options.show === undefined ? true : options.show;
        /**
         * @private
         * @type {Array.<number>}
         */
        this.offset_ = options.offset ? options.offset : [0, -40];
        /**
             * @private
             * @type {string}
             */
        this.anchor_ = options.anchor ? options.anchor : undefined;
        /**
         * @private
         * @type {import('./Marker').default|null}
         */
        this.marker_ = null;
        /**
         * @private
         * @type {import('maplibre-gl').Popup|null}
         */
        this.mbPopup_ = null;
        /**
         * @private
         * @type {Element|null|undefined}
         */
        this.element_ = null;
        this.initialize_(options);
    }
    /**
     * @private
     * @param {LabelOptions} options Label options
     */
    Label.prototype.initialize_ = function (options) {
        var strStyle = (options.color ? "color:".concat(options.color, ";") : '')
            + (this.background_ ? "background:".concat(options.background, ";") : '')
            + (this.fontWeight_ ? 'font-weight:bold' : '');
        this.mbPopup_ = new maplibregl.Popup({
            className: "cercalia-label ".concat(this.show_ ? 'visible' : 'hidden').concat(this.className_ ? " ".concat(this.className_) : ''),
            maxWidth: 'none',
            closeButton: false,
            closeOnClick: false,
            offset: this.offset_,
            anchor: this.anchor_,
        });
        this.mbPopup_.setHTML("\n      <div id=\"".concat(this.id_, "\" class=\"content\">\n        <div class=\"text\" style=\"").concat(strStyle, "\">").concat(this.text_, "</div>\n      </div>\n    "));
        this.element_ = this.mbPopup_._content;
    };
    /**
     * Change label text content. It can be HTML string. For example `<b>Bold</b> text label`
     * @param {string} text Text to display on label.
     * @api
     */
    Label.prototype.setText = function (text) {
        this.text_ = text;
        this.element_.querySelector('.text').innerHTML = this.text_;
    };
    /**
     * Obtain text
     * @return {string} Label text
     * @api
     */
    Label.prototype.getText = function () {
        return this.text_;
    };
    /**
     * Show element on map
     * @api
     */
    Label.prototype.show = function () {
        this.show_ = true;
        this.mbPopup_.removeClassName('hidden');
        this.mbPopup_.addClassName('visible');
    };
    /**
     * Hide element on map
     * @api
     */
    Label.prototype.hide = function () {
        this.show_ = false;
        this.mbPopup_.removeClassName('visible');
        this.mbPopup_.addClassName('hidden');
    };
    /**
     * @return {boolean} if popup is opened or not. Values: `true` or `false`.
     * @api
     */
    Label.prototype.isVisible = function () {
        return this.show_;
    };
    /**
     * @param {import('./Marker').default} marker Marker.
     */
    Label.prototype.setMarker = function (marker) {
        this.marker_ = marker;
        this.mbPopup_.setLngLat(this.marker_.getPosition().toMapboxLngLat());
    };
    /**
     * Obtain marker.
     * @return {import('./Marker').default} Marker.
     * @api
     */
    Label.prototype.getMarker = function () {
        return this.marker_;
    };
    /**
     * Change label position.
     * @param {import('./LonLat').default} position Position
     * @api
     */
    Label.prototype.setPosition = function (position) {
        this.mbPopup_.setLngLat(position.toMapboxLngLat());
    };
    /**
     * @return {import('maplibre-gl').Popup} Mapbox popup
     */
    Label.prototype.getMapboxPopup = function () {
        return this.mbPopup_;
    };
    /**
     * Return DOM Element.
     * @return {Element}
     * @api
     */
    Label.prototype.getElement = function () {
        return this.element_;
    };
    return Label;
}());
export default Label;
//# sourceMappingURL=Label.js.map